import { Link } from 'react-router-dom'
import { Archive, ArrowUpRight, Check, Inbox, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useI18n } from '@/i18n'
import { Container } from '@/components/layout/Container'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { EmptyState, Skeleton } from '@/components/ui/Feedback'
import { RequireOwner } from '@/components/auth/RequireOwner'
import { StudioNav } from '@/components/studio/StudioNav'
import { StatCard } from '@/components/studio/StatCard'
import { useOwner } from '@/hooks/useOwner'
import { useWatches } from '@/hooks/useWatches'
import { useEnquiries } from '@/hooks/useEnquiries'
import { initials } from '@/lib/format'
import type { BadgeTone, EnquiryStatus } from '@/types'

const enquiryTone: Record<EnquiryStatus, BadgeTone> = {
  new: 'primary',
  answered: 'success',
  archived: 'neutral',
}

/**
 * Studio home: the five numbers that matter, then the inbox.
 *
 * The inbox lives here rather than on a page of its own because answering a
 * customer is the only thing in this studio that is genuinely time-sensitive —
 * it should be visible the moment the owner signs in.
 */
export default function StudioOverview() {
  return (
    <RequireOwner>
      <Overview />
    </RequireOwner>
  )
}

function Overview() {
  const { t, tf, formatPrice, formatNumber, timeAgo } = useI18n()
  const { user } = useOwner()
  const { watches, loading } = useWatches()
  const { enquiries, loading: loadingEnquiries, setStatus } = useEnquiries()

  const published = watches.filter((w) => w.status === 'published')
  const drafts = watches.filter((w) => w.status === 'draft')
  const closed = watches.filter((w) => w.status === 'reserved' || w.status === 'sold')
  const catalogueValue = published.reduce((sum, w) => sum + (w.price || 0) * Math.max(w.stock, 1), 0)
  const newEnquiries = enquiries.filter((e) => e.status === 'new')

  const recentListings = watches
    .slice()
    .sort((a, b) => new Date(b.created_at ?? 0).getTime() - new Date(a.created_at ?? 0).getTime())
    .slice(0, 4)

  return (
    <Container size="xl" data-upbo-id="page-studio-overview" data-upbo-label="Studio overview">
      <div className="pt-10 pb-6 flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-2">
          <span className="eyebrow">{t('studio.eyebrow')}</span>
          <h1 className="font-display text-[36px] sm:text-[44px] leading-tight">
            {t('studio.welcome', { name: user?.name || user?.email?.split('@')[0] || '' })}
          </h1>
          <p className="text-sm text-text-muted">{t('studio.welcomeBody')}</p>
        </div>
        <Link to="/studio/new">
          <Button icon={<Plus size={15} strokeWidth={1.5} />}>{t('studio.newListing')}</Button>
        </Link>
      </div>

      <StudioNav />

      {/* ── The numbers ────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 py-8">
        {loading ? (
          Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-[118px] w-full" />)
        ) : (
          <>
            <StatCard label={t('studio.statPublished')} value={formatNumber(published.length)} />
            <StatCard label={t('studio.statDraft')} value={formatNumber(drafts.length)} />
            <StatCard label={t('studio.statReserved')} value={formatNumber(closed.length)} />
            <StatCard label={t('studio.statValue')} value={formatPrice(catalogueValue)} />
            <StatCard
              label={t('studio.statEnquiries')}
              value={formatNumber(newEnquiries.length)}
              accent={newEnquiries.length > 0}
            />
          </>
        )}
      </div>

      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-10 lg:gap-14 pb-20">
        {/* ── Inbox ────────────────────────────────────────────────────── */}
        <section data-upbo-type="section" data-upbo-label="Enquiry inbox" className="flex flex-col gap-5">
          <div className="flex items-end justify-between gap-4">
            <div className="flex flex-col gap-1">
              <h2 className="font-display text-[28px] leading-tight">{t('studio.inboxTitle')}</h2>
              <p className="text-[13px] text-text-muted">{t('studio.inboxSubtitle')}</p>
            </div>
          </div>

          {loadingEnquiries ? (
            <div className="flex flex-col gap-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-24 w-full" />
              ))}
            </div>
          ) : enquiries.length === 0 ? (
            <EmptyState
              icon={<Inbox size={22} strokeWidth={1.5} />}
              title={t('studio.inboxEmpty')}
              description={t('studio.inboxEmptyBody')}
            />
          ) : (
            <ul className="flex flex-col">
              {enquiries.map((e) => (
                <li
                  key={e.id}
                  data-upbo-type="card"
                  data-upbo-label={`Enquiry — ${e.name}`}
                  className={cn(
                    'flex gap-4 py-5 border-b border-border',
                    e.status === 'archived' && 'opacity-55',
                  )}
                >
                  {/* Initials rather than a stock portrait: these are real people. */}
                  <span className="h-10 w-10 shrink-0 grid place-items-center rounded-full border border-border text-[12px] tracking-wider text-text-muted">
                    {initials(e.name)}
                  </span>

                  <div className="flex-1 min-w-0 flex flex-col gap-1.5">
                    <div className="flex flex-wrap items-center gap-2.5">
                      <span className="text-sm text-text">{e.name}</span>
                      <Badge tone={enquiryTone[e.status]} size="sm">
                        {t(`studio.enquiry${e.status.charAt(0).toUpperCase()}${e.status.slice(1)}`)}
                      </Badge>
                      <span className="text-[11px] text-text-faint">{timeAgo(e.created_at ?? '')}</span>
                    </div>

                    {e.watch_title && (
                      <span className="text-[11px] uppercase tracking-[0.14em] text-text-faint">
                        {t('studio.aboutPiece')}:{' '}
                        {e.watch_id ? (
                          <Link to={`/collection/${e.watch_id}`} className="hover:text-text transition-colors">
                            {e.watch_title}
                          </Link>
                        ) : (
                          e.watch_title
                        )}
                      </span>
                    )}

                    <p className="text-[14px] text-text-muted leading-relaxed">{e.message}</p>

                    <div className="flex flex-wrap items-center gap-4 mt-1.5">
                      <a
                        href={`mailto:${e.email}`}
                        className="text-[12px] text-text-muted hover:text-text transition-colors link-underline"
                      >
                        {e.email}
                      </a>
                      {e.phone && <span className="text-[12px] text-text-faint">{e.phone}</span>}

                      {e.status !== 'answered' && (
                        <button
                          type="button"
                          onClick={() => void setStatus(e.id, 'answered')}
                          className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] text-text-muted hover:text-text transition-colors"
                        >
                          <Check size={13} strokeWidth={1.5} />
                          {t('studio.markAnswered')}
                        </button>
                      )}
                      {e.status !== 'archived' && (
                        <button
                          type="button"
                          onClick={() => void setStatus(e.id, 'archived')}
                          className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] text-text-muted hover:text-text transition-colors"
                        >
                          <Archive size={13} strokeWidth={1.5} />
                          {t('studio.markArchived')}
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* ── Recently posted ──────────────────────────────────────────── */}
        <section data-upbo-type="section" data-upbo-label="Recent listings" className="flex flex-col gap-5">
          <h2 className="font-display text-[28px] leading-tight">{t('studio.recentListings')}</h2>

          <ul className="flex flex-col">
            {recentListings.map((w) => (
              <li key={w.id} className="flex items-center gap-4 py-4 border-b border-border">
                <img
                  src={w.image}
                  alt=""
                  loading="lazy"
                  className="h-14 w-14 object-cover rounded-squircle bg-surface-2"
                />
                <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                  <span className="text-sm text-text truncate">{tf(w, 'title')}</span>
                  <span className="text-[11px] uppercase tracking-[0.14em] text-text-faint">
                    {t(`status.${w.status}`)} · {formatPrice(w.price)}
                  </span>
                </div>
                <Link
                  to={`/studio/new?id=${w.id}`}
                  aria-label={t('common.edit')}
                  className="text-text-faint hover:text-text transition-colors"
                >
                  <ArrowUpRight size={17} strokeWidth={1.5} />
                </Link>
              </li>
            ))}
          </ul>

          <Link to="/studio/listings" className="w-fit">
            <Button variant="outline" size="sm">
              {t('studio.manageTitle')}
            </Button>
          </Link>
        </section>
      </div>
    </Container>
  )
}
